import {
  Box,
  Button,
  Flex,
  Input,
  Select,
  Spinner,
  Text,
  Badge,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  VStack,
  HStack,
  Divider,
  Code,
  useColorModeValue,
  Stack,
} from '@chakra-ui/react';
import { useState } from 'react';
import { FaEye, FaSearch } from 'react-icons/fa';
import { configApi } from '../../../services/configApi';
import { useQuery } from '@tanstack/react-query';
import CardHeaderMobile from '../../../components/Mobile/header';

interface IAuditLog {
  id: string;
  user_id: string;
  role: string;
  action: string;
  level: 'info' | 'warning' | 'error' | 'critical';
  category: string;
  resource_type: string;
  resource_id?: string;
  selective_process_id?: string;
  step_id?: string;
  message: string;
  ip_address?: string;
  user_agent?: string;
  timestamp: string;
  context?: Record<string, unknown>;
}

interface IAuditSearchParams {
  start_date?: string;
  end_date?: string;
  level?: string;
  category?: string;
  action?: string;
  resource_type?: string;
  page?: number;
  page_size?: number;
}

interface IAuditSearchResponse {
  results: IAuditLog[];
  count: number;
  next: string | null;
  previous: string | null;
}

const AuditLogsMobilePage: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedLog, setSelectedLog] = useState<IAuditLog | null>(null);
  const [filters, setFilters] = useState<IAuditSearchParams>({
    page: 1,
    page_size: 10,
  });
  const [showFilters, setShowFilters] = useState(false);

  const bgCard = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Fetch audit logs
  const { data, isLoading, refetch } = useQuery<IAuditSearchResponse>({
    queryKey: ['audit-logs', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, String(value));
      });
      const response = await configApi.get(`/audit/my-logs/?${params.toString()}`);
      return response.data;
    },
    retry: 1,
  });

  const handleViewDetails = (log: IAuditLog) => {
    setSelectedLog(log);
    onOpen();
  };

  const getLevelColor = (level: string) => {
    const colors = {
      info: 'blue',
      warning: 'orange',
      error: 'red',
      critical: 'purple',
    };
    return colors[level as keyof typeof colors] || 'gray';
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return timestamp;
    }
  };

  const handleClearFilters = () => {
    setFilters({ page: 1, page_size: 10 });
    setShowFilters(false);
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <CardHeaderMobile badgeText="Auditoria" title="Meus Logs" subTitle="Histórico de Atividades" />

      <Box p={4}>
        {/* Botão de Filtros */}
        <Button
          w="full"
          mb={4}
          onClick={() => setShowFilters(!showFilters)}
          variant="outline"
          size="sm"
        >
          {showFilters ? 'Ocultar' : 'Mostrar'} Filtros
        </Button>

        {/* Filtros */}
        {showFilters && (
          <Box bg={bgCard} borderRadius="md" p={4} mb={4} borderWidth="1px" borderColor={borderColor}>
            <VStack spacing={3} align="stretch">
              <Box>
                <Text fontSize="xs" mb={1} fontWeight="semibold">Data Inicial</Text>
                <Input
                  type="date"
                  size="sm"
                  value={filters.start_date || ''}
                  onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                />
              </Box>

              <Box>
                <Text fontSize="xs" mb={1} fontWeight="semibold">Data Final</Text>
                <Input
                  type="date"
                  size="sm"
                  value={filters.end_date || ''}
                  onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                />
              </Box>

              <Box>
                <Text fontSize="xs" mb={1} fontWeight="semibold">Nível</Text>
                <Select
                  size="sm"
                  value={filters.level || ''}
                  onChange={(e) => setFilters({ ...filters, level: e.target.value })}
                >
                  <option value="">Todos</option>
                  <option value="info">Info</option>
                  <option value="warning">Warning</option>
                  <option value="error">Error</option>
                  <option value="critical">Critical</option>
                </Select>
              </Box>

              <Box>
                <Text fontSize="xs" mb={1} fontWeight="semibold">Categoria</Text>
                <Select
                  size="sm"
                  value={filters.category || ''}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                >
                  <option value="">Todas</option>
                  <option value="authentication">Autenticação</option>
                  <option value="profile_management">Perfil</option>
                  <option value="registration">Inscrição</option>
                  <option value="document_management">Documentos</option>
                  <option value="step_progress">Etapas</option>
                  <option value="data_export">Exportação</option>
                  <option value="system">Sistema</option>
                </Select>
              </Box>

              <HStack spacing={2}>
                <Button
                  leftIcon={<FaSearch />}
                  colorScheme="blue"
                  size="sm"
                  flex="1"
                  onClick={() => refetch()}
                  isLoading={isLoading}
                >
                  Buscar
                </Button>
                <Button size="sm" variant="outline" flex="1" onClick={handleClearFilters}>
                  Limpar
                </Button>
              </HStack>
            </VStack>
          </Box>
        )}

        {/* Lista de Logs */}
        {isLoading ? (
          <Flex justify="center" align="center" minH="200px">
            <Spinner size="lg" />
          </Flex>
        ) : (
          <>
            <VStack spacing={3} align="stretch">
              {data?.results?.map((log) => (
                <Box
                  key={log.id}
                  bg={bgCard}
                  borderRadius="md"
                  p={3}
                  borderWidth="1px"
                  borderColor={borderColor}
                  position="relative"
                >
                  <HStack justify="space-between" mb={2}>
                    <Badge colorScheme={getLevelColor(log.level)} fontSize="xs">
                      {log.level.toUpperCase()}
                    </Badge>
                    <Text fontSize="xs" color="gray.500">
                      {formatTimestamp(log.timestamp)}
                    </Text>
                  </HStack>

                  <HStack justify="space-between" mb={2}>
                    <Badge colorScheme="cyan" fontSize="xs">
                      {log.action}
                    </Badge>
                    <Text fontSize="xs" color="gray.600">
                      {log.category}
                    </Text>
                  </HStack>

                  <Text fontSize="sm" mb={2} noOfLines={2}>
                    {log.message}
                  </Text>

                  <HStack justify="space-between">
                    <Text fontSize="xs" color="gray.500">
                      {log.resource_type}
                    </Text>
                    <IconButton
                      aria-label="Ver detalhes"
                      icon={<FaEye />}
                      size="xs"
                      colorScheme="blue"
                      onClick={() => handleViewDetails(log)}
                    />
                  </HStack>
                </Box>
              ))}
            </VStack>

            {/* Paginação */}
            <Flex justify="space-between" align="center" mt={4}>
              <Text fontSize="xs">
                {data?.results?.length || 0} de {data?.count || 0}
              </Text>
              <HStack spacing={2}>
                <Button
                  size="xs"
                  onClick={() => setFilters({ ...filters, page: (filters.page || 1) - 1 })}
                  isDisabled={!data?.previous || isLoading}
                >
                  Anterior
                </Button>
                <Text fontSize="xs">Pág. {filters.page || 1}</Text>
                <Button
                  size="xs"
                  onClick={() => setFilters({ ...filters, page: (filters.page || 1) + 1 })}
                  isDisabled={!data?.next || isLoading}
                >
                  Próxima
                </Button>
              </HStack>
            </Flex>
          </>
        )}
      </Box>

      {/* Modal de Detalhes */}
      <Modal isOpen={isOpen} onClose={onClose} size="full">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">📋 Detalhes do Log</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedLog && (
              <VStack align="stretch" spacing={4}>
                {/* Informações Básicas */}
                <Box>
                  <Text fontWeight="bold" mb={3}>
                    Informações Básicas
                  </Text>
                  <Stack spacing={2} fontSize="sm">
                    <HStack justify="space-between">
                      <Text fontWeight="semibold">ID:</Text>
                      <Code fontSize="xs">{selectedLog.id.slice(0, 8)}...</Code>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontWeight="semibold">Data/Hora:</Text>
                      <Text fontSize="xs">{formatTimestamp(selectedLog.timestamp)}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontWeight="semibold">Ação:</Text>
                      <Badge colorScheme="cyan" fontSize="xs">{selectedLog.action}</Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontWeight="semibold">Nível:</Text>
                      <Badge colorScheme={getLevelColor(selectedLog.level)} fontSize="xs">
                        {selectedLog.level.toUpperCase()}
                      </Badge>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontWeight="semibold">Categoria:</Text>
                      <Text fontSize="xs">{selectedLog.category}</Text>
                    </HStack>
                  </Stack>
                </Box>

                <Divider />

                {/* Recursos */}
                <Box>
                  <Text fontWeight="bold" mb={3}>
                    Recursos
                  </Text>
                  <Stack spacing={2} fontSize="sm">
                    <HStack justify="space-between">
                      <Text fontWeight="semibold">Tipo:</Text>
                      <Text fontSize="xs">{selectedLog.resource_type}</Text>
                    </HStack>
                    {selectedLog.resource_id && (
                      <HStack justify="space-between">
                        <Text fontWeight="semibold">ID:</Text>
                        <Code fontSize="xs">{selectedLog.resource_id.slice(0, 8)}...</Code>
                      </HStack>
                    )}
                  </Stack>
                </Box>

                <Divider />

                {/* Mensagem */}
                <Box>
                  <Text fontWeight="bold" mb={3}>
                    Mensagem
                  </Text>
                  <Box p={3} bg="gray.50" borderRadius="md" borderWidth="1px">
                    <Text fontSize="sm" whiteSpace="pre-wrap">
                      {selectedLog.message}
                    </Text>
                  </Box>
                </Box>

                {/* Contexto Adicional */}
                {selectedLog.context && Object.keys(selectedLog.context).length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Text fontWeight="bold" mb={3}>
                        Contexto Adicional
                      </Text>
                      <Code
                        display="block"
                        p={3}
                        fontSize="xs"
                        whiteSpace="pre"
                        overflowX="auto"
                      >
                        {JSON.stringify(selectedLog.context, null, 2)}
                      </Code>
                    </Box>
                  </>
                )}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AuditLogsMobilePage;
